/**
 * Interpreta uma data que veio do backend (string ISO) ou já pronta
 * (Date/epoch). Campo sem fuso explícito (tem "T" mas não termina em Z nem
 * em offset +HH:MM/-HH:MM) é tratado como UTC -- compat com respostas
 * antigas. Quando o fuso já vem explícito (Z, ou offset depois da fatia de
 * fuso das rotas de histórico), a regex já casa e NÃO acrescenta nada: um
 * "Z" duplicado (ex.: "...+00:00" virando "...+00:00Z") quebraria o parse.
 * Único ponto de parsing pros campos de created_at/updated_at do backend —
 * quem precisar do Date "cru" (não só formatado) importa esta função em vez
 * de repetir `new Date(campo)` (CreatedCell.jsx, BoardActivityModal.jsx).
 */
export function parseDate(date) {
  if (!date) return null;
  let s = typeof date === 'string' ? date : null;
  if (s && s.includes('T') && !/Z$|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = s + 'Z';
  }
  const d = new Date(s ?? date);
  return isNaN(d.getTime()) ? null : d;
}

// Data e hora absolutas em pt-BR (ex.: "18/09/2026 14:32"). Usada nos
// tooltips (title=) onde hoje só existe tempo relativo, e como fallback
// de formatRelative quando o horário está no futuro.
export function formatAbsolute(date) {
  const d = parseDate(date);
  if (!d) return '';
  const datePart = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timePart = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${datePart} ${timePart}`;
}

export default function formatRelative(date) {
  const d = parseDate(date);
  if (!d) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  // Horário no futuro (tipicamente erro de fuso) não é "agora" — cai no
  // formato absoluto em vez de esconder o problema com um clamp em zero.
  if (diff < 0) return formatAbsolute(d);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)}d`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
